import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Plus, Edit3, Trash2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyForm = {
  name: '',
  claims_email: '',
  phone: '',
  payment_terms: '',
  notes: '',
  active: true,
};

// iter85 — Warranty Companies admin (System Settings tab).
// Tab is rendered inside AdminSettings with a custom shape: this component owns its own data
// (CRUD via /api/admin/warranty-companies) and ignores the generic settings save bar above.
const WarrantyCompaniesForm = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/warranty-companies`, { withCredentials: true });
      setItems(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load warranty companies');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (it) => {
    setEditing(it);
    setForm({
      name: it.name || '',
      claims_email: it.claims_email || '',
      phone: it.phone || '',
      payment_terms: it.payment_terms || '',
      notes: it.notes || '',
      active: it.active !== false,
    });
    setDialogOpen(true);
  };

  const save = async (e) => {
    e?.preventDefault();
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        claims_email: form.claims_email.trim() || null,
        phone: form.phone.trim(),
        payment_terms: form.payment_terms.trim(),
        notes: form.notes.trim(),
        active: !!form.active,
      };
      if (editing) {
        await axios.put(`${API}/admin/warranty-companies/${editing.id}`, body, { withCredentials: true });
        toast.success('Warranty company updated');
      } else {
        await axios.post(`${API}/admin/warranty-companies`, body, { withCredentials: true });
        toast.success('Warranty company created');
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const remove = async (it) => {
    const verb = it.system ? 'Deactivate' : 'Delete';
    const note = it.system
      ? `"${it.name}" is a system default. It cannot be deleted, but you can deactivate it so it stops appearing in pricebook dropdowns.`
      : `Delete warranty company "${it.name}"? If it is referenced by any pricebook item or customer it will be deactivated instead of removed.`;
    if (!window.confirm(`${verb} "${it.name}"?\n\n${note}`)) return;
    try {
      const { data } = await axios.delete(`${API}/admin/warranty-companies/${it.id}`, { withCredentials: true });
      if (data.soft_deleted) {
        toast.info(`Deactivated (still referenced by ${data.pricebook_refs || 0} pricebook item${data.pricebook_refs === 1 ? '' : 's'} and ${data.customer_refs || 0} customer${data.customer_refs === 1 ? '' : 's'}).`);
      } else {
        toast.success('Deleted');
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const toggleActive = async (it, next) => {
    try {
      await axios.put(`${API}/admin/warranty-companies/${it.id}`, {
        name: it.name,
        claims_email: it.claims_email,
        phone: it.phone || '',
        payment_terms: it.payment_terms || '',
        notes: it.notes || '',
        active: next,
      }, { withCredentials: true });
      toast.success(next ? 'Activated' : 'Deactivated');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  return (
    <div data-testid="warranty-companies-form" className="bg-[#141414] border border-[#262626] rounded-md p-6 space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-white font-medium flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#D4AF37]" /> Warranty Companies</h3>
          <p className="text-[#A3A3A3] text-sm mt-1">
            Manage secondary payors used in Pricebook pricing tiers and on customer profiles
            (HSP, manufacturer warranties, builder warranties, etc.). The homeowner is always the
            primary customer — these are the secondary payors attached to a job.
          </p>
        </div>
        <Button onClick={openCreate} data-testid="add-warranty-company" className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">
          <Plus className="w-4 h-4 mr-2" /> Add Warranty Company
        </Button>
      </div>

      <div className="bg-[#0A0A0A] border border-[#262626] rounded-md overflow-hidden">
        {loading ? (
          <div className="p-6 text-[#A3A3A3] text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div data-testid="warranty-companies-empty" className="p-8 text-center text-[#A3A3A3] text-sm">No warranty companies yet. Click "Add Warranty Company" to add the first one.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0A] text-[#A3A3A3] text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-2">Name</th>
                <th className="text-left px-4 py-2">Claims Email</th>
                <th className="text-left px-4 py-2">Phone</th>
                <th className="text-left px-4 py-2">Terms</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} data-testid={`wc-row-${it.id}`} className="border-t border-[#262626] hover:bg-[#1a1a1a]">
                  <td className="px-4 py-2 text-white">
                    <div className="flex items-center gap-2">
                      {it.name}
                      {it.system && <Badge className="bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/30 text-[10px]">SYSTEM</Badge>}
                    </div>
                    {it.notes && <p className="text-[#525252] text-xs mt-0.5">{it.notes}</p>}
                  </td>
                  <td className="px-4 py-2 text-[#A3A3A3] text-xs">{it.claims_email || '—'}</td>
                  <td className="px-4 py-2 text-[#A3A3A3] text-xs">{it.phone || '—'}</td>
                  <td className="px-4 py-2 text-[#A3A3A3] text-xs">{it.payment_terms || '—'}</td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={it.active !== false}
                        onCheckedChange={(v) => toggleActive(it, v)}
                        data-testid={`wc-toggle-${it.id}`}
                      />
                      <span className={`text-xs ${it.active !== false ? 'text-green-400' : 'text-[#A3A3A3]'}`}>
                        {it.active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="inline-flex gap-1">
                      <Button data-testid={`wc-edit-${it.id}`} size="sm" variant="outline" onClick={() => openEdit(it)} className="border-[#262626] text-white hover:bg-[#262626]"><Edit3 className="w-3.5 h-3.5" /></Button>
                      <Button data-testid={`wc-delete-${it.id}`} size="sm" variant="outline" onClick={() => remove(it)} className="border-red-700 text-red-400 hover:bg-red-900/30"><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#141414] border-[#262626] text-white max-w-md" style={{ scrollbarGutter: 'stable' }}>
          <DialogHeader><DialogTitle>{editing ? 'Edit Warranty Company' : 'New Warranty Company'}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div>
              <Label className="text-white">Name</Label>
              <Input data-testid="wc-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="HSP, Manufacturer Warranty, …" className="bg-[#0A0A0A] border-[#262626] text-white h-11 text-base" />
            </div>
            <div>
              <Label className="text-white">Claims Email</Label>
              <Input data-testid="wc-claims-email" type="email" value={form.claims_email} onChange={(e) => setForm({ ...form, claims_email: e.target.value })} placeholder="claims@hsp.com" className="bg-[#0A0A0A] border-[#262626] text-white h-11 text-base" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white">Phone</Label>
                <Input data-testid="wc-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white h-11 text-base" />
              </div>
              <div>
                <Label className="text-white">Payment Terms</Label>
                <Input data-testid="wc-payment-terms" value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} placeholder="NET 30" className="bg-[#0A0A0A] border-[#262626] text-white h-11 text-base" />
              </div>
            </div>
            <div>
              <Label className="text-white">Notes</Label>
              <Textarea data-testid="wc-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white text-base" />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={!!form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="wc-active" />
              <span className="text-white text-sm">Active (appears in pricebook + customer dropdowns)</span>
            </div>
            <div className="flex gap-2 pt-1">
              <Button data-testid="wc-save" type="submit" disabled={saving} className="flex-1 h-11 bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">{saving ? 'Saving…' : (editing ? 'Save Changes' : 'Create')}</Button>
              <Button type="button" onClick={() => setDialogOpen(false)} variant="outline" className="border-[#262626] text-white hover:bg-[#262626] h-11">Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WarrantyCompaniesForm;
